import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User } from '../types';
import { authApi, TokenManager } from '../services/api';
import { firebaseApiService } from '../services/firebaseApi';
import { userApiService } from '../services/userApi';
import { ReferralManager } from '../utils/referralManager';
import { User as FirebaseUser } from 'firebase/auth';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (name: string, phone: string, address?: string) => Promise<boolean>;
  loginWithFirebaseVerification: (name: string, firebaseUser: FirebaseUser, address?: string) => Promise<boolean>;
  appLogin: (token: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload,
      };
    case 'LOGOUT':
      TokenManager.clearTokens(); // Clear tokens from localStorage
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Handle referral code submission if needed
  const handleReferralCodeSubmission = async (user: User) => {
    try {
      // Check if we have a referral code in localStorage and user is new or doesn't have refBy set
      const refBy = ReferralManager.getRefBy();
      
      if (refBy && user) {
        console.log('Found refBy in localStorage, attempting to submit:', refBy);
        
        // Submit referral code
        const referralResponse = await userApiService.submitReferralCode(refBy);
        
        if (referralResponse.success) {
          console.log('Referral code submitted successfully');
          // Clear refBy from localStorage after successful submission
          ReferralManager.clearRefBy();
        } else {
          console.error('Failed to submit referral code:', referralResponse.error);
          // Keep refBy in localStorage for retry later
        }
      }
    } catch (error) {
      console.error('Error handling referral code submission:', error);
      // Keep refBy in localStorage for retry later
    }
  };

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      // Check if we have valid tokens first
      if (!authApi.isAuthenticated()) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Chưa đăng nhập' });
        return;
      }

      dispatch({ type: 'AUTH_START' });
      try {
        const response = await authApi.checkAuth();
        if (response.success && response.data) {
          dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
          
          // Handle referral code submission after successful auth check
          await handleReferralCodeSubmission(response.data.user);
        } else {
          dispatch({ type: 'AUTH_FAILURE', payload: 'Chưa đăng nhập' });
        }
      } catch (error) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Chưa đăng nhập' });
        // Clear tokens if auth check fails
        TokenManager.clearTokens();
      }
    };

    checkAuth();
  }, []);

  const login = async (name: string, phone: string, address?: string): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const loginData: { name: string; phone?: string; address?: string } = { name };
      if (phone) loginData.phone = phone;
      if (address) loginData.address = address;
      
      const response = await authApi.webLogin(loginData);
      if (response.success && response.data) {
        // Save JWT tokens if provided - check both possible structures
        if (response.data.tokens) {
          // Structure: { tokens: { accessToken, refreshToken, expiresIn } }
          TokenManager.setTokens(
            response.data.tokens.accessToken,
            response.data.tokens.refreshToken
          );
        } else if (response.data.accessToken && response.data.refreshToken) {
          // Structure: { accessToken, refreshToken, expiresIn } directly
          TokenManager.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
        
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
        
        // Handle referral code submission after successful login
        await handleReferralCodeSubmission(response.data.user);
        
        return true;
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'Đăng nhập thất bại' });
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Lỗi đăng nhập';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      return false;
    }
  };

  const loginWithFirebaseVerification = async (name: string, firebaseUser: FirebaseUser, address?: string): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });
    try {
      // First, login to our system
      const phone = firebaseUser.phoneNumber || `firebase_${firebaseUser.uid.substring(0, 8)}`;
      const loginData: { name: string; phone?: string; address?: string } = { name, phone };
      if (address) loginData.address = address;
      
      const response = await authApi.webLogin(loginData);
      if (response.success && response.data) {
        // Save JWT tokens if provided - check both possible structures
        if (response.data.tokens) {
          // Structure: { tokens: { accessToken, refreshToken, expiresIn } }
          TokenManager.setTokens(
            response.data.tokens.accessToken,
            response.data.tokens.refreshToken
          );
        } else if (response.data.accessToken && response.data.refreshToken) {
          // Structure: { accessToken, refreshToken, expiresIn } directly
          TokenManager.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }

        // Then, link Firebase verification to the user
        try {
          const firebaseResponse = await firebaseApiService.verifyPhoneAuth(response.data.user.id);
          if (firebaseResponse.success) {
            console.log('Firebase verification linked successfully');
          }
        } catch (firebaseError) {
          console.error('Firebase verification linking failed:', firebaseError);
          // Continue anyway, user is still logged in
        }
        
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
        
        // Handle referral code submission after successful Firebase login
        await handleReferralCodeSubmission(response.data.user);
        
        return true;
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'Đăng nhập thất bại' });
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Lỗi đăng nhập';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      return false;
    }
  };

  const appLogin = async (token: string): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const response = await authApi.appLoginWithToken(token);
      
      if (response.success && response.data) {
        // Store tokens - check both possible structures
        if (response.data.tokens) {
          // Structure: { tokens: { accessToken, refreshToken, expiresIn } }
          TokenManager.setTokens(
            response.data.tokens.accessToken,
            response.data.tokens.refreshToken
          );
        } else if (response.data.accessToken && response.data.refreshToken) {
          // Structure: { accessToken, refreshToken, expiresIn } directly
          TokenManager.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
        
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
        
        // Handle referral code submission after successful app login
        await handleReferralCodeSubmission(response.data.user);
        
        return true;
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'App login thất bại' });
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Lỗi app login';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshUser = async (): Promise<void> => {
    if (!authApi.isAuthenticated()) {
      return;
    }

    try {
      const response = await authApi.checkAuth();
      if (response.success && response.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user });
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      // Don't dispatch failure here as it might log out the user unnecessarily
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithFirebaseVerification,
    appLogin,
    logout,
    refreshUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
export default AuthProvider;
